
import {Flex, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue, Select, Icon, Button, InputRightElement, InputGroup, Input, Checkbox, } from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useGlobalFilter, usePagination, useSortBy, useTable} from "react-table";
import {Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, RefreshCw} from "lucide-react";
import BeatLoader from "react-spinners/BeatLoader";
import axios from "axios";

// Custom components
import Card from "components/card/Card";
import TableMenu from "views/global/TableMenu";
import { QuickAlert } from "theme/components/alerts";
import { Link } from "react-router-dom";
import { columnValueLabel } from "lib/labels";




export default function SimpleDataTable(props) {


  //PROPS
  const {src, title, menuItems, buttons, search, checkbox, checkboxMenu, tableMenu, external, additionalOptions, trigger} = props;
  //STATES
  const [currentPage, setCurrentPage] = useState(1);
  const [sortings, setSortings] = useState({sort: "id", sortby: "desc"});
  const [searchKeyword, setSearchKeyword] = useState(null);
  const [tableData, setTableData] = useState({
    columns: [],
    rows: {
      data: [],
      links: [],
      total: 0
    }
  });
  const [contentLoading, setContentLoading] = useState(false);
  const [checked, setChecked] = useState({});
  const [unchecked, setUnchecked] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const abortControllerRef = useRef(null);

  //IMPORT STATES
  const { setAlert, clearAlert, showAlert } = QuickAlert();

  //VARIABLES
  const getToken = JSON.parse(localStorage.getItem('token'));
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const headerBorderColor = useColorModeValue("#E6ECFA", "rgba(135, 140, 189, 0.3)");
  const theadBg = useColorModeValue("#fbfbfb", "whiteAlpha.50");
  const rowBorderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const mutedTextColor = useColorModeValue("gray.500", "gray.400");
  const brandColor = useColorModeValue("brand.500", "brand.300");
  const alignText = title ? null : "flex-end" ;
  const checkboxAction = Object.keys(checked).filter((key) => checked[key] === true).length;
  const totalCount = tableData.rows.total;


  //GENERATE EXTERNAL LINKS
  const externalLinkGenerator =(link, type) => {

    const matches = link.match(/\[(.*?)\]/g);
    matches.map((match) => {
      const key = match.replace("[","").replace("]","");
      link = link.replace(match, type[key]);
    });
    return link;
  }


  //GENERATE SPECIAL CLASS
  const generateClass = (string) => {
    if(string === null) return null;
    if(!isNaN(string)) return "data-table-single-integer";
    const sanitizedValue = string.replace(/[^a-zA-Z\s]/g, ''); // Keep only alphabetic characters and spaces
    const classes = "data-table-single-"+sanitizedValue.split(' ')[0].toLowerCase()
    return classes;
  };
  
  //GET DATA FROM API
  const getContactList = () =>{
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    setContentLoading(true);
    setChecked({});
    return axios.get(process.env.REACT_APP_API+src, {
      signal: abortControllerRef.current.signal,
      params: {
        perPage:perPage,
        page:currentPage,
        sortType:sortings.sort,
        sortData:sortings.sortby,
        search:searchKeyword,
        ...additionalOptions
      },
      headers: {
        'Content-Type': 'application/json',
        'Accept' : 'application/json',
        'Authorization': `Bearer ${getToken}`
      }
    }).then(response => {
      return response;
    }).then(response => {
      setTableData(response.data);
      setContentLoading(false);
    }).catch(function(error) {
      if (axios.isCancel(error)) return;
      setContentLoading(false);
      setAlert("error",error.response.data.errors);
    });
  };

  //MOUNT DATA EVERYTIME STATE CHANGES
  useEffect(() => {
    getContactList();
    return () => abortControllerRef.current?.abort();
  }, [currentPage,sortings,perPage, searchKeyword, trigger]);

  //TABLE VARIABLES
  const columns = useMemo(() => tableData.columns, [tableData.columns]);
  const data = useMemo(() => tableData.rows.data, [tableData.rows.data]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 1000 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
  } = tableInstance;

  //HANDLER TO CHECKED BOXES
  const handleCheckboxChange = (status, id) => {
    setChecked((prev) => ({
        ...prev,
        [id]: status,
    }));
  };

  //HANDLER TO CLEAR CHECKED BOXES
  const handleClear = () => {
    return () => {
      setUnchecked(true);
    }
  }
  
  const allCheckboxHandler = (checked) => {
    tableData.rows.data.map((row) => {
      row.checked = checked;
      handleCheckboxChange(checked, row.id)
    });
  }

  useEffect(() => {
    tableData.rows.data.map((row) => {
      row.checked = false;
      handleCheckboxChange(false, row.id)
    });
    setUnchecked(false);
  }, [unchecked])

  //HANDLE SEARCH BAR
  const handleSearch = (keyword) => {
    if(keyword.length >= 3) {
        setSearchKeyword(keyword);
    }else if(keyword.length <= 0 && searchKeyword !== null) {
        setSearchKeyword(null);
    }
  };

  //HANDLE PAGE CHANGE
  const handlePageChange = (page) => {
      setCurrentPage(page);
  }

  //HANDLE PER PAGE CHANGE
  const handlePerPageChange = (perPage) => {
      setPerPage(perPage);
      const MaxPage = Math.ceil(totalCount / perPage);
      if(MaxPage < currentPage){
          setCurrentPage(MaxPage);
      }
  }
  //HANDLE SORT CHANGE
  const handleSortChange = (id, by) => {
      setSortings({sort: id, sortby: by});
  }

  //TABLE BODY COMPONENT
  const TableBody = () => {
    return (
      <Tbody {...getTableBodyProps()}>
       
        {page.map((row, index) => {
          prepareRow(row);
          return (
            <Tr {...row.getRowProps()} key={index}>
              { checkbox ? <Td
              fontSize={{ sm: "14px" }}
              borderColor={rowBorderColor}
              py='14px'
              ps='24px'
              width='40px'
              >
                <Checkbox 
                key = {index}
                onChange={
                  (e) => {
                    handleCheckboxChange(e.target.checked, row.original.id)
                    row.original.checked = e.target.checked;
                  }
                }
                isChecked={row.original.checked}
                />
              </Td> : null}
              {row.cells.map((cell, index) => {
                //cell.column.id
                return (
                  <Td
                    {...cell.getCellProps()}
                    key={index}
                    fontSize={{ sm: "14px" }}
                    maxW='300px'
                    py='14px'
                    ps={index === 0 && !checkbox ? '24px' : undefined}
                    pe={index === row.cells.length - 1 && !tableMenu ? '24px' : undefined}
                    borderColor={rowBorderColor}>
                    <Flex align='start'>
                      <Text
                        color={textColor}
                        fontSize='sm'
                        fontWeight='500'
                        whiteSpace='normal'
                        wordBreak='break-word'
                        className={"data-table-item-"+cell.column.id +" "+(generateClass(cell.value))}>
                        {external ?
                        Object.entries(external).map(([key, link]) => {

                          if(cell.column.id === key){
                            return (
                              <Link
                              key={index}
                              to={externalLinkGenerator(link,row.original)}
                              className="primary-color fw-bold"
                              >
                                {columnValueLabel(cell.column.id, cell.value)}
                              </Link>
                            )
                          }else{
                            return columnValueLabel(cell.column.id, cell.value);
                          }
                        })
                        : columnValueLabel(cell.column.id, cell.value)}
                      </Text>
                    </Flex>
                  </Td>
                );
              })}
              {tableMenu ? <Td
              fontSize={{ sm: "14px" }}
              borderColor={rowBorderColor}
              py='14px'
              pe='24px'
              width='80px'
              >
              <TableMenu menuItems={tableMenu.map(item => ({
                  ...item,
                  title: typeof item.title === 'function' ? item.title(row.original) : item.title,
                  icon: typeof item.icon === 'function' ? item.icon(row.original) : item.icon,
              }))} row={row.original} />
              </Td> : null}
            </Tr>
          );
        })}
      </Tbody>
    )
};

return (
  <Card
    direction='column'
    w='100%'
    p='0'
    overflowX={{ sm: "scroll", lg: "hidden" }}>

    {title || menuItems || buttons ? <Flex justify='space-between' pt='20px' pb='16px' px='24px' borderBottom='1px solid' borderColor={headerBorderColor} align='center' justifyContent={alignText}>

      { title ? <Text
        color={textColor}
        fontSize='22px'
        fontWeight='700'
        lineHeight='100%'>
        {title}
      </Text> : null}

      <Flex align='center'>
        {buttons && buttons.map((button, index) => (
          <Button
            key={index}
            variant={button.variant || "outline"}
            colorScheme={button.colorScheme || "brand"}
            fontWeight="light"
            fontSize="14px"
            leftIcon={button.icon}
            onClick={button.handle}
            isDisabled={button.disabled}
            me={index === buttons.length - 1 && !menuItems ? 0 : "10px"}
          >
            {button.title}
          </Button>
        ))}
        { menuItems ? <TableMenu menuItems={menuItems} /> : null}
      </Flex>

    </Flex>: null}

    {search || checkboxAction ? <Flex justify='space-between' align='center' px='24px' pt='16px' pb='16px'>
      <Flex
        w="50%"
        maxW="325px"
        justify='space-between'
        align='center'
      >
      { search ? <InputGroup size="md">
        <Input
          pr="4.5rem"
          type="text"
          placeholder="cerca..."
          borderRadius="8px"
          _focus={{borderColor: "gray.300"}}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <InputRightElement width="2.5rem">
          <Icon as={Search} color="gray.300" fontSize="22px"  />
        </InputRightElement>
      </InputGroup> : null}
      </Flex>
      <Flex
       justify='space-between'
       align='center'>
        {checkboxAction  ?
          Object.keys(checkboxMenu).map((key, index) => {
            return (
              <Button
                leftIcon={checkboxMenu[key].icon}
                variant="outline"
                me={2}
                fontWeight="light"
                colorScheme={checkboxMenu[key].color}
                onClick={ () => checkboxMenu[key].handle(checked)}
                key={index}
                >
                {checkboxMenu[key].name}
              </Button>
            )
          })
        : null}
        {checkboxAction  ?
          <Button
            leftIcon={<RefreshCw />}
            variant="outline"
            me={2}
            colorScheme="gray"
            onClick={handleClear()}
            fontWeight="light"
            >
            Clear
          </Button>
        : null}
      </Flex>
    </Flex> : null}

    <Table {...getTableProps()} variant='simple' color='gray.500' mb='0' w='100%'>
      <Thead>
        {headerGroups.map((headerGroup, index) => (
          <Tr {...headerGroup.getHeaderGroupProps()} key={index} bg={theadBg} borderTop='1px solid' borderColor={headerBorderColor}>
            {checkbox ? <Th
            ps='24px'
            pe='10px'
            py='14px'
            borderColor={borderColor}
            width='40px'
            >
              <Checkbox
                onChange={
                  (e) => {
                    allCheckboxHandler(e.target.checked)
                  }
                }
                isChecked={tableData.rows.data.every((row) => row.checked)}
              />
            </Th> : null}
            {headerGroup.headers.map((column, index) => (
              <Th
                ps={index === 0 && !checkbox ? '24px' : undefined}
                pe={index === headerGroup.headers.length - 1 && !tableMenu ? '24px' : '10px'}
                py='14px'
                key={index}
                borderColor={borderColor}
                textTransform='none'
                cursor="pointer"
                maxW='300px'
                onClick={() => handleSortChange(column.id, sortings.sortby==="desc" ? "asc" : "desc")}
                >
                <Flex
                  align='center'
                  fontSize='sm'
                  fontWeight='600'
                  color={textColor}
                  >
                  {column.render("Header")} {sortings.sortby==="asc" && sortings.sort===column.id ? <ChevronDown size={16} /> : sortings.sortby==="desc" && sortings.sort===column.id ? <ChevronUp size={16} /> : null}
                </Flex>
              </Th>
            ))}
            {tableMenu ? <Th
            pe='24px'
            py='14px'
            borderColor={borderColor}
            width='80px'
            textTransform='none'
            >
            </Th> : null}
          </Tr>
        ))}
      </Thead>
     {contentLoading===false ? TableBody() : null}
    </Table>
    <Flex justifyContent="center" px='24px' pb={totalCount === 0 || contentLoading ? '20px' : '0'}>
        {contentLoading===true ? <BeatLoader
        loading="true"
        size={15}
        aria-label="Loading Spinner"
        color="#422CFA"
        />:  totalCount === 0 ?
          <Text
          width="100%"
          textAlign="center"
          color="gray.400"
          >
            Nessun risultato trovato
          </Text>
          : null}
     </Flex>

    {totalCount > 0 ? <Flex justify='space-between' align='center' pt='16px' pb='16px' px='24px' borderTop='1px solid' borderColor={headerBorderColor}>
      <Flex align='center'>
        <Text fontSize='sm' color={mutedTextColor} me='10px'>Righe per pagina</Text>
        <Select
          size='sm'
          w='72px'
          borderRadius='8px'
          borderColor={headerBorderColor}
          _focus={{ borderColor: "gray.300" }}
          defaultValue={perPage}
          onChange={(e) => handlePerPageChange(e.target.value)}
        >
            <option>10</option>
            <option>25</option>
            <option>50</option>
            <option>100</option>
            <option>500</option>
        </Select>
      </Flex>
      <Flex align='center'>
        <Text fontSize='sm' color={mutedTextColor} me='16px'>
          {currentPage === 1 ? 1 : ((currentPage - 1) *  perPage) +1} - {(currentPage *  perPage) > totalCount ? totalCount : currentPage *  perPage} di {totalCount}
        </Text>
        <Button
          variant='ghost'
          size='sm'
          minW='32px'
          p='0'
          isDisabled={currentPage <= 1}
          onClick={() => handlePageChange(currentPage - 1)}
          me='4px'
        >
          <Icon as={ChevronLeft} boxSize='16px' />
        </Button>
        {tableData.rows.links &&
          Object.entries(tableData.rows.links).map(([key, value]) => {
            // Prev/next arrows are rendered separately; skip them whatever the locale.
            if (String(value.label).includes("&laquo;") || String(value.label).includes("&raquo;")) return null;
            return (
              <Button
              variant={value.active ? "solid" : "ghost"}
              colorScheme={value.active ? "brand" : "gray"}
              size="sm"
              minW='32px'
              px='0'
              me='4px'
              key={key}
              onClick={() => handlePageChange(value.label)}
              >
                {value.label}
              </Button>
            )
          })
        }
        <Button
          variant='ghost'
          size='sm'
          minW='32px'
          p='0'
          isDisabled={currentPage >= Math.ceil(totalCount / perPage)}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          <Icon as={ChevronRight} boxSize='16px' />
        </Button>
      </Flex>
    </Flex> : null }
  </Card>
);

}
